/**
 * React Query hooks for Excel Processing API
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { excelApi } from '../services/excel';
import { api } from '../services/api';
import type { UploadRequestBody, ParseExcelRequest, ParseExcelResponse, ParseExcelJobResponse, ParseProgressResponse } from '../types/api';

const STALE_JOB_MS = 15 * 60 * 1000; // 15 minutes — matches backend threshold
const ETA_MIN_FILES = 5; // Don't show ETA until this many files processed

// =============================================================================
// Query Keys
// =============================================================================

export const excelKeys = {
  all: ['excel'] as const,
  parseStatus: (jobId: string) => [...excelKeys.all, 'parse-status', jobId] as const,
};

// =============================================================================
// Queries
// =============================================================================

/**
 * Hook to poll Excel parsing status
 *
 * Usage:
 * ```
 * const { data, isLoading } = useParseStatus(jobId, {
 *   enabled: !!jobId,
 *   refetchInterval: (data) => data?.status === 'processing' ? 2000 : false,
 * });
 * ```
 */
export function useParseStatus(
  jobId: string,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false | ((query: { state: { data?: ParseProgressResponse } }) => number | false);
    onSuccess?: (data: ParseProgressResponse) => void;
  }
) {
  return useQuery({
    queryKey: excelKeys.parseStatus(jobId),
    queryFn: () => excelApi.getParseStatus(jobId),
    enabled: options?.enabled ?? !!jobId,
    refetchInterval: options?.refetchInterval,
  });
}

// =============================================================================
// Mutations
// =============================================================================

/**
 * Hook to get presigned upload URL for Excel file
 */
export function useGetUploadUrl() {
  return useMutation({
    mutationFn: (request: UploadRequestBody) => excelApi.getUploadUrl(request),
  });
}

/**
 * Hook to upload file to S3 using presigned URL
 */
export function useUploadFile() {
  return useMutation({
    mutationFn: ({ presignedUrl, file }: { presignedUrl: string; file: File }) =>
      excelApi.uploadFile(presignedUrl, file),
  });
}

/**
 * Hook to parse Excel files from SharePoint (wizard Step 3)
 *
 * Usage:
 * ```
 * const parseExcel = useParseExcelFromSharePoint();
 *
 * const result = await parseExcel.mutateAsync({
 *   files: [
 *     { path: "/TestPlans/Scenario1.xlsx", drive_id: "...", name: "Scenario1.xlsx" }
 *   ]
 * });
 * ```
 */
export function useParseExcelFromSharePoint() {
  return useMutation({
    mutationFn: async (data: ParseExcelRequest) => {
      return api.post<ParseExcelJobResponse>('/admin/excel/parse', data);
    },
  });
}

/**
 * Hook to manage async Excel parse progress and result fetching.
 *
 * Usage:
 * ```
 * const { startJob, progress, result, error, isPolling, isComplete } = useExcelParseProgress();
 * // After getting job_id from parseExcel.mutateAsync():
 * startJob(jobId);
 * // progress updates automatically via polling
 * // result is set when job completes and S3 result is fetched
 * ```
 */
export function useExcelParseProgress() {
  const [jobId, setJobId] = useState<string | null>(null);
  const [result, setResult] = useState<ParseExcelResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isFetchingResult, setIsFetchingResult] = useState(false);
  const [etaSeconds, setEtaSeconds] = useState<number | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const firstParsedRef = useRef<number>(0);

  const { data: progress } = useParseStatus(jobId ?? '', {
    enabled: !!jobId && !result && !error,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return 2000;
      if (data.status === 'completed' || data.status === 'failed') return false;
      // Stale job detection — stop polling if no update in 15 min
      if (data.updated_at) {
        const updatedAt = new Date(data.updated_at).getTime();
        if (Date.now() - updatedAt > STALE_JOB_MS) return false;
      }
      return 2000;
    },
  });

  // ETA calculation — track rate of files_parsed change
  useEffect(() => {
    if (!progress || progress.status !== 'processing') return;
    const parsed = progress.files_parsed ?? 0;
    const total = progress.total_files ?? 0;

    // Record when we first see progress
    if (!startTimeRef.current && parsed > 0) {
      startTimeRef.current = Date.now();
      firstParsedRef.current = parsed;
    }

    // Only compute ETA after enough data points
    if (startTimeRef.current && parsed >= firstParsedRef.current + ETA_MIN_FILES && total > 0) {
      const elapsed = (Date.now() - startTimeRef.current) / 1000;
      const filesSinceStart = parsed - firstParsedRef.current;
      const rate = filesSinceStart / elapsed;
      const remaining = total - parsed;
      setEtaSeconds(rate > 0 ? Math.ceil(remaining / rate) : null);
    }
  }, [progress]);

  // When status becomes 'completed', fetch result from S3
  useEffect(() => {
    if (progress?.status === 'completed' && progress.result_url && !result && !isFetchingResult) {
      setIsFetchingResult(true);
      excelApi.fetchParseResult(progress.result_url)
        .then(data => {
          setResult(data);
          setIsFetchingResult(false);
        })
        .catch(err => {
          setError(err instanceof Error ? err.message : 'Failed to fetch results');
          setIsFetchingResult(false);
        });
    } else if (progress?.status === 'failed') {
      setError(progress.error_message || 'Parse job failed');
    }
  }, [progress, result, isFetchingResult]);

  const startJob = useCallback((id: string) => {
    setJobId(id);
    setResult(null);
    setError(null);
    setIsFetchingResult(false);
    setEtaSeconds(null);
    startTimeRef.current = null;
    firstParsedRef.current = 0;
  }, []);

  const reset = useCallback(() => {
    setJobId(null);
    setResult(null);
    setError(null);
    setIsFetchingResult(false);
    setEtaSeconds(null);
    startTimeRef.current = null;
    firstParsedRef.current = 0;
  }, []);

  return {
    startJob,
    reset,
    jobId,
    progress,
    result,
    error,
    etaSeconds,
    isPolling: !!jobId && !result && !error,
    isFetchingResult,
    isComplete: !!result,
  };
}

